import { BrLoading, BrSelect } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Button, Modal, Table } from 'react-dsgov';
import { useNotificacao } from '../../../../../../../../../hooks/useNotificacao';
import {
  IConfiguracaoOperacao,
  IConfiguracaoOutrosRegimes,
  IRequisicaoSalvarConfiguracao,
} from '../../../../../../../../../interfaces/IOperacoes';
import { IProcessosADeclararListaSuspensa } from '../../../../../../../../../interfaces/IProcessos';
import { formatarDataISOpBR } from '../../../../../../../../../utils/formatDate';
import { formatarNrProcesso } from '../../../../../../../../../utils/formatNrProcesso';
import { useBackEndRequests } from '../../../../../../../lib/backEndRequests';
import {
  ETabTitularArrendatario,
  IEstadoTitularArrendatario,
} from '../../../../../../../lib/titularArrendatarioEstadoProcesso';
import styles from './situacaoProcesso.module.scss';
import InfoCircleTooltip from '../../../../../../../../../components/infoCircleTooltip/infoCircleTooltip';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

export enum EDisparoSalvarConfiguracao {
  NAO_DISPARAR = 0,
  MANTER_TELA = 1,
  PROXIMA_TELA = 2,
  AUTOMATICO = 3,
}

interface IProps {
  triggerSalvarConfiguracao: boolean;
  declaracaoId: string;
  changeStep: Function;
  setIsEdicaoOutrosRegimes: React.Dispatch<React.SetStateAction<boolean>>;
  estadoTela: IEstadoTitularArrendatario | null;
  setEstadoTela: React.Dispatch<React.SetStateAction<IEstadoTitularArrendatario | null>>;
}

interface IHeader {
  field: string;
  label: string;
  format?: Function; //Usa a propriedade da coluna
  value?: string | Function; //Usa toda a linha
}

const SituacaoProcesso = ({
  triggerSalvarConfiguracao = false,
  declaracaoId,
  changeStep,
  setIsEdicaoOutrosRegimes,
  estadoTela,
  setEstadoTela,
}: IProps) => {
  const { postListaIdProcessosADeclarar, postSalvarConfiguracao, getProcessosADeclarar } = useBackEndRequests();
  const mostrarNotificacao = useNotificacao();

  const [configuracaoProcessos, setConfiguracaoProcessos] = useState<IConfiguracaoOperacao[]>([]);
  const [possuiOperacoesCadastradas, setPossuiOperacoesCadastradas] = useState(true);
  const [possuiConfiguracaoSalva, setPossuiConfiguracaoSalva] = useState(true);
  const [isLoadingOverlayOpen, setIsLoadingOverlayOpen] = useState(false);
  const [isModalSalvarConfiguracaoOpen, setIsModalSalvarConfiguracaoOpen] = useState(false);
  const [isLimparConfiguracao, setIsLimparConfiguracao] = useState(false);

  const [opcoes, setOpcoes] = useState<{ label: string; value: string }[]>([]);
  const [listaIdProcessos, setListaIdProcessos] = useState<string[]>([]);

  const isManterTela = useRef(false);

  const {
    data: retornoConfiguracao,
    isLoading,
    isFetching,
    refetch: refetchConfiguracao,
    isRefetching: isRefetchingConfiguracao,
    error,
  } = useQuery<IConfiguracaoOutrosRegimes, IErrorWithSentryId>({
    queryKey: ['postListaIdProcessosADeclarar', declaracaoId],
    queryFn: () => postListaIdProcessosADeclarar(declaracaoId, listaIdProcessos),
    enabled: false,
    staleTime: 0,
    retry: false,
    gcTime: 0,
  });

  const {
    data: listaIdProcessosData,
    isLoading: isLoadingListaIdProcessos,
    isFetching: isFetchingListaIdProcessos,
    refetch: refetchListaIdProcessos,
    error: errorListaIdProcessos,
  } = useQuery<IProcessosADeclararListaSuspensa[], IErrorWithSentryId>({
    queryKey: ['getProcessosADeclarar', declaracaoId],
    queryFn: () => getProcessosADeclarar(declaracaoId),
    enabled: false,
    staleTime: 0,
    retry: false,
    gcTime: 0,
  });

  useEffect(() => {
    if (retornoConfiguracao) {
      setConfiguracaoProcessos(retornoConfiguracao.processos);
      setPossuiOperacoesCadastradas(retornoConfiguracao.possuiOperacoesCadastradas);
      setPossuiConfiguracaoSalva(retornoConfiguracao.possuiConfiguracaoSalva);
    } else if ((retornoConfiguracao !== undefined && !retornoConfiguracao) || !!error) {
      const errorResponse = error?.response?.data as any;

      mostrarNotificacao({
        tipo: 'danger',
        conteudo: errorResponse.message || 'Erro ao recuperar os dados',
        ...(errorResponse && { complementoSentryId: errorResponse }),
      });
    }
  }, [retornoConfiguracao, error]);

  useEffect(() => {
    const fetchListaIdProcessos = async () => {
      try {
        const response = await refetchListaIdProcessos();
        if (response.data) {
          setOpcoes(mapProcessosListadosToOpcoes(response.data));
          setListaIdProcessos(
            response.data
              .filter((processo) => processo.isSelecionado)
              .map((processo) => processo.processoId?.toString())
          );
        }
      } catch {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao carregar os processos a declarar',
        });
      }
    };

    fetchListaIdProcessos();
  }, []);

  // Caso seja primeira vez que a tela é carregada e não há o retorno de configuração nem processo marcado na lista obtida na API
  useEffect(() => {
    if (!retornoConfiguracao && isNecessarioRefetchConfiguracao()) {
      refetchConfiguracao();
    }
  }, [listaIdProcessos]);

  //Dispara o evento de salvarConfiguração de forma externa
  useEffect(() => {
    if (
      triggerSalvarConfiguracao === true &&
      possuiConfiguracaoSalva === false &&
      isLoading === false &&
      isFetching === false
    ) {
      dispararSalvarConfiguracao(EDisparoSalvarConfiguracao.AUTOMATICO);
    } else if (triggerSalvarConfiguracao === true && possuiConfiguracaoSalva === true) {
      abrirListagemOperacao();
    }
  }, [triggerSalvarConfiguracao]);

  // Dispara o salvamento da configuração ao limpar a configuração
  useEffect(() => {
    if (isLimparConfiguracao) {
      dispararSalvarConfiguracao(EDisparoSalvarConfiguracao.NAO_DISPARAR);
      setIsLimparConfiguracao(false);
    }
  }, [isLimparConfiguracao, configuracaoProcessos]);

  const abrirListagemOperacao = () => {
    changeStep();
    atualizarEstadoTela();
  };

  const atualizarEstadoTela = () => {
    const novoEstado = {
      telaSelecionada: ETabTitularArrendatario.OUTROS_REGIMES_LISTAGEM,
    };

    setEstadoTela(novoEstado);
  };

  const dispararSalvarConfiguracao = (tipoDisparo: EDisparoSalvarConfiguracao) => {
    switch (tipoDisparo) {
      case EDisparoSalvarConfiguracao.MANTER_TELA:
        salvarConfiguracao(false);
        break;

      case EDisparoSalvarConfiguracao.PROXIMA_TELA:
        salvarConfiguracao(true);
        break;

      case EDisparoSalvarConfiguracao.AUTOMATICO:
        salvarConfiguracao(true, true);
        break;

      default:
        setIsModalSalvarConfiguracaoOpen(false);
        break;
    }
  };

  const onClickSalvar = (manterTela: boolean) => {
    isManterTela.current = manterTela;
    validarSalvarConfiguracao();
  };

  const onClickSalvarSemConfiguracao = () => {
    mostrarNotificacao({
      tipo: 'success',
      conteudo:
        'A configuração inicial foi apagada, para concluir a operação, salve a configuração por meio dos botões "Salvar",\n "Salvar e Listar Operações" ou navegue à próxima seção "2. Listagem de Operações".',
    });
    limparConfiguracao();
  };

  const validarSalvarConfiguracao = () => {
    if (possuiOperacoesCadastradas) {
      setIsModalSalvarConfiguracaoOpen(true);
    } else {
      confirmarOperacao();
    }
  };

  const mapProcessosListadosToOpcoes = (listaProcessos: IProcessosADeclararListaSuspensa[]) => {
    if (!listaProcessos || !Array.isArray(listaProcessos)) {
      return [];
    }
    return listaProcessos.map((processo) => ({
      label: processo?.numeroProcesso,
      value: processo.processoId?.toString(),
    }));
  };

  const onClickListarProcessos = (listaIdProcessos: string[]) => {
    if (listaIdProcessos.length === 0) {
      mostrarNotificacao({
        tipo: 'warning',
        conteudo: 'Selecione pelo menos um processo para listar.',
      });
      return;
    }

    refetchConfiguracao()
      .then(() => {})
      .catch(() => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao listar processos',
        });
      })
      .finally(() => {
        setIsLoadingOverlayOpen(false);
      });
  };

  const confirmarOperacaoLimparConfiguracao = () => {
    limparConfiguracao();
  };

  const confirmarOperacao = () => {
    let tipoDisparo = EDisparoSalvarConfiguracao.PROXIMA_TELA;

    if (isManterTela.current) {
      tipoDisparo = EDisparoSalvarConfiguracao.MANTER_TELA;
    }

    dispararSalvarConfiguracao(tipoDisparo);
  };

  const limparConfiguracao = () => {
    setIsLimparConfiguracao(true);
    setConfiguracaoProcessos([]);
    setListaIdProcessos([]);
    setPossuiConfiguracaoSalva(false);
  };

  const salvarConfiguracao = async (mudarStep: boolean, isAutomatico?: boolean) => {
    const processosRequest = structuredClone(configuracaoProcessos);

    processosRequest.forEach((processo: IConfiguracaoOperacao) => {
      processo.isSelecionado = listaIdProcessos.includes(processo.processoMinerarioId.toString());
    });

    const configuracaoRequest: IRequisicaoSalvarConfiguracao = {
      declaracaoId,
      configuracoes: processosRequest,
    };

    setIsModalSalvarConfiguracaoOpen(false);
    setIsLoadingOverlayOpen(true);

    await postSalvarConfiguracao(configuracaoRequest)
      .then(() => {
        let mensagemSucesso = 'Configuração Salva com Sucesso!';

        if (isAutomatico) {
          mensagemSucesso = 'Configuração Inicial foi Salva Automaticamente';
        }

        mostrarNotificacao({
          tipo: 'success',
          conteudo: mensagemSucesso,
        });

        if (mudarStep) {
          abrirListagemOperacao();
        }
      })
      .catch(() => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao Salvar a Configuração',
        });
      })
      .finally(() => {
        setIsLoadingOverlayOpen(false);
      });
  };

  const formatarVigencia = (processo: IConfiguracaoOperacao) => {
    const dataInicio = formatarDataISOpBR(processo.dataVigenciaInicio ?? '');
    const dataFim = formatarDataISOpBR(processo.dataVigenciaFim ?? '');

    //Data fim ausente
    if (dataFim === '') {
      //Sem título
      if (dataInicio === '') {
        return '-';
      }

      return dataInicio;

      //Todas as datas preenchidas
    } else {
      return `${dataInicio} a ${dataFim}`;
    }
  };

  const formatarSwitch = (processo: IConfiguracaoOperacao, atributo: 'houveFatoGerador' | 'houveProducao') => {
    const atualizarAtributo = () => {
      processo[atributo] = !processo[atributo];
      const novaListagem = structuredClone(configuracaoProcessos);

      setConfiguracaoProcessos(novaListagem);
    };

    const onKeyDownAtualizarAtributo = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        atualizarAtributo();
      } else {
        event.stopPropagation();
      }
    };

    return (
      <div
        className={`br-switch icon`}
        aria-checked={processo[atributo]}
        tabIndex={0}
        onClick={atualizarAtributo}
        onKeyDown={onKeyDownAtualizarAtributo}
      >
        <input
          id='switch-icon'
          type='checkbox'
          name='switch-icon'
          checked={processo[atributo]}
          role='switch'
          disabled={true}
        />
        <label>{processo[atributo] ? 'Sim' : 'Não'}</label>
      </div>
    );
  };

  const formatarFatoGerador = (processo: IConfiguracaoOperacao) => {
    return formatarSwitch(processo, 'houveFatoGerador');
  };

  const formatarProducao = (processo: IConfiguracaoOperacao) => {
    return formatarSwitch(processo, 'houveProducao');
  };

  const quebrarTexto = (texto: string, separador = ';') => {
    if (texto === null || texto === undefined || texto === '') {
      return '-';
    }

    return texto.split(separador).map((parte, index) => (
      <span key={index}>
        {parte}
        {index < texto.length - 1 && <br />}
      </span>
    ));
  };

  const isNecessarioRefetchConfiguracao = () => {
    return (
      !!listaIdProcessosData &&
      listaIdProcessosData.length > 0 &&
      listaIdProcessosData.filter((processo) => processo?.isSelecionado).length > 0 &&
      retornoConfiguracao === undefined
    );
  };

  const isLoadingOrFetching = () => {
    if (
      isLoading ||
      isFetching ||
      isLoadingListaIdProcessos ||
      isFetchingListaIdProcessos ||
      isRefetchingConfiguracao
    ) {
      return true;
    }
    return false;
  };

  const TABELA_CABECALHO: IHeader[] = [
    {
      field: 'numeroProcesso',
      label: 'Processo Minerário N°',
      format: formatarNrProcesso,
    },
    { field: 'vigencia', label: 'Vigência do Título', value: formatarVigencia },
    {
      field: 'tipoTitulo',
      label: 'Tipo do Título',
      format: quebrarTexto,
    },
    {
      field: 'houveFatoGerador',
      label: 'Houve Fato Gerador da CFEM?',
      value: formatarFatoGerador,
    },
    {
      field: 'houveProducao',
      label: 'Houve Produção de Bem Mineral?',
      value: formatarProducao,
    },
  ];

  return (
    <div data-testid='situacao-processo'>
      <Modal title='Deseja realmente continuar?' useScrim modalOpened={isModalSalvarConfiguracaoOpen}>
        <Modal.Body className={styles.modalContent}>
          Ao prosseguir com essa operação, se você atualizar um processo sem fato gerador e sem produção,{' '}
          <strong>TODAS</strong> as operações cadastradas para este processo serão <strong>EXCLUÍDAS</strong> e os
          valores serão redefinidos para zero.
        </Modal.Body>
        <Modal.Footer justify-content='end'>
          <Button
            secondary
            small
            m={2}
            onClick={() => dispararSalvarConfiguracao(EDisparoSalvarConfiguracao.NAO_DISPARAR)}
          >
            Cancelar
          </Button>
          <Button primary small m={2} onClick={() => confirmarOperacao()}>
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>
      {errorListaIdProcessos && (
        <div className={styles.listaSuspensaContainer}>
          <div className={styles.selectErro}>
            <BrSelect
              label={'Selecione os processos minerários que tiveram Fato Gerador e/ou Produção'}
              id={'id-select-processos-a-declarar'}
              data-testid={'id-select-processos-a-declarar'}
              placeholder='Erro ao carregar processos'
              value={[]}
              options={[]}
              disabled
            />
            <span className={styles.errorText}>Falha na consulta de Processo</span>
            <span
              className={styles.linkText}
              onClick={() => refetchListaIdProcessos()}
              onKeyDown={() => refetchListaIdProcessos()}
            >
              (Refazer consulta)
            </span>
          </div>
          <div className={styles.btn}></div>
        </div>
      )}
      {!errorListaIdProcessos && !isLoadingOrFetching() && !listaIdProcessosData?.length && (
        <div className={styles.listaSuspensaContainer}>
          <div className={styles.selectErro}>
            <BrSelect
              label={'Selecione os processos minerários que tiveram Fato Gerador e/ou Produção'}
              id={'id-select-processos-a-declarar'}
              data-testid={'id-select-processos-a-declarar'}
              placeholder='Nenhum processo disponível para declaração'
              value={[]}
              options={[]}
              disabled
            />
          </div>
          <div className={styles.btn}></div>
        </div>
      )}

      {!errorListaIdProcessos &&
        !isLoadingOrFetching() &&
        !isNecessarioRefetchConfiguracao() &&
        listaIdProcessosData &&
        listaIdProcessosData?.length > 0 && (
          <div className={styles.listaSuspensaContainer}>
            <div className={styles.select}>
              <div className={styles.inputIconWrapper}>
                <i className={`fas fa-search ${styles.icon}`} />
                <BrSelect
                  label={'Selecione os processos minerários que tiveram Fato Gerador e/ou Produção'}
                  id={'id-select-processos-a-declarar'}
                  data-testid={'id-select-processos-a-declarar'}
                  key={opcoes?.map((opcao) => opcao?.value).join('-')}
                  placeholder={
                    listaIdProcessos.length === 0 ? 'Digite e selecione um processo...' : 'Selecione os processos'
                  }
                  value={listaIdProcessos}
                  onClick={(e) => {
                    if (e.target instanceof HTMLInputElement && e.target.ariaLabel === 'Selecionar todos') {
                      let idSelectAllInput = e.target.id;
                      let labelSelectAll = document.querySelector(`label[for="${idSelectAllInput}"]`);
                      if (labelSelectAll) {
                        if (e.target.checked) {
                          setListaIdProcessos(opcoes?.map((opcao) => opcao?.value) ?? []);
                        } else {
                          setListaIdProcessos([]);
                        }
                      }
                    }
                  }}
                  onChange={(value: string[] | null) => {
                    setListaIdProcessos(value ?? []);
                  }}
                  options={opcoes}
                  disabled={false}
                  type={'multiple'}
                />
              </div>
              <label className={styles.tooltipLabel} data-testid='tooltip'>
                <InfoCircleTooltip
                  text={
                    'Caso o processo desejado não esteja na lista ou apresente alguma inconsistência nas informações cadastrais, favor entrar em contato com a ANM por meio dos canais de atendimento.'
                  }
                  id={'lista-suspensa-processos-tooltip'}
                  variant='info'
                />
              </label>
            </div>
            <div className={styles.btn}>
              <Button secondary onClick={() => onClickListarProcessos(listaIdProcessos)}>
                <i className={`fas fa-arrow-right fa-md`}></i>
                Listar Processos
              </Button>
            </div>
          </div>
        )}

      {isLoadingOverlayOpen && (
        <div className={styles.loadingOverlay}>
          <BrLoading large label='Processando Operação...' />
        </div>
      )}

      {(isLoadingOrFetching() || isNecessarioRefetchConfiguracao()) && (
        <div className={styles.loading}>
          <BrLoading large label='Carregando...' />
        </div>
      )}

      {!isLoadingOrFetching() &&
        !isNecessarioRefetchConfiguracao() &&
        configuracaoProcessos &&
        configuracaoProcessos.length > 0 && (
          <div className={styles.tabela}>
            <Table
              showTopBar={false}
              headers={TABELA_CABECALHO}
              showPagination={false}
              showDensityButtons={false}
              showSearch={false}
              density='medium'
              tabIndex={0}
            >
              <tbody>
                {configuracaoProcessos.map((row: any, index: any) => (
                  <tr key={index}>
                    {TABELA_CABECALHO.map((header) => (
                      <td key={header.field} align='center'>
                        {typeof header?.value === 'function' && header?.value(row)}
                        {header.format && header.format(row[header.field])}

                        {header?.value == null && header.format == null && row[header.field]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

      {!isLoadingOrFetching() && (
        <div className={styles.linhaBotoes}>
          {listaIdProcessos.length > 0 && (
            <div className={styles.btnLimpar}>
              <Button secondary onClick={() => onClickSalvarSemConfiguracao()}>
                <i className={`fas fa-trash mr-1 `}></i>
                Limpar configurações
              </Button>
            </div>
          )}
          <span
            title={
              listaIdProcessosData && listaIdProcessosData?.length === 0
                ? 'Não existem processos disponíveis. Não é possível salvar.'
                : undefined
            }
          >
            <Button
              onClick={() => onClickSalvar(true)}
              disabled={listaIdProcessosData && listaIdProcessosData?.length === 0}
            >
              <i className={`fas fa-save fa-md`}></i>
              Salvar
            </Button>
          </span>
          <span
            title={
              listaIdProcessosData && listaIdProcessosData?.length === 0
                ? 'Não existem processos disponíveis. Não é possível salvar.'
                : undefined
            }
          >
            <Button
              primary
              onClick={() => onClickSalvar(false)}
              disabled={listaIdProcessosData && listaIdProcessosData?.length === 0}
            >
              <i className={`fas fa-arrow-right fa-md`}></i>
              Salvar e Listar Operações
            </Button>
          </span>
        </div>
      )}
    </div>
  );
};

export default SituacaoProcesso;
